import { useState, useCallback, useEffect } from "react";
import {
  DocumentListFilters,
  DocumentEntity,
  GetPendingDocumentsUseCase,
  GetCompletedDocumentsUseCase,
} from "../../domain";
import { DocumentRepositoryImpl } from "../../data/repositories/DocumentRepositoryImpl";

// Create repository instance (singleton)
const documentRepository = new DocumentRepositoryImpl();

// Create use cases
const getPendingDocumentsUseCase = new GetPendingDocumentsUseCase(
  documentRepository
);
const getCompletedDocumentsUseCase = new GetCompletedDocumentsUseCase(
  documentRepository
);

export interface UseDocumentsReturn {
  // Pending documents
  pendingDocuments: DocumentEntity[];
  pendingPagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  };
  pendingLoading: boolean;
  fetchPending: (page: number) => Promise<void>;

  // Completed documents
  completedDocuments: DocumentEntity[];
  completedPagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  };
  completedLoading: boolean;
  fetchCompleted: (page: number) => Promise<void>;

  // Fetch both in parallel
  fetchAllDocuments: (
    pendingPage?: number,
    completedPage?: number
  ) => Promise<void>;

  // Fetch both silently (without loading states) - for auto-refresh
  fetchAllDocumentsSilent: (
    pendingPage?: number,
    completedPage?: number
  ) => Promise<void>;

  // Common
  error: string | null;
  clearError: () => void;
}

export const useDocuments = (
  itemsPerPage: number = 10,
  pendingFilters: DocumentListFilters = {},
  completedFilters: DocumentListFilters = {}
): UseDocumentsReturn => {
  // Pending documents state
  const [pendingDocuments, setPendingDocuments] = useState<DocumentEntity[]>(
    []
  );
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(0);
  const [pendingTotalItems, setPendingTotalItems] = useState(0);
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Completed documents state
  const [completedDocuments, setCompletedDocuments] = useState<
    DocumentEntity[]
  >([]);
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
  const [completedTotalPages, setCompletedTotalPages] = useState(0);
  const [completedTotalItems, setCompletedTotalItems] = useState(0);
  const [completedHasMore, setCompletedHasMore] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);

  // Common state
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch pending documents
   */
  const fetchPending = useCallback(
    async (page: number): Promise<void> => {
      setPendingLoading(true);
      setError(null);

      try {
        const result = await getPendingDocumentsUseCase.execute(
          page,
          itemsPerPage,
          pendingFilters
        );

        setPendingDocuments(result.documents);
        setPendingCurrentPage(result.pagination.currentPage);
        setPendingTotalPages(result.pagination.totalPages);
        setPendingTotalItems(result.pagination.total);
        setPendingHasMore(result.pagination.hasMore);
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to load pending documents";
        setError(errorMessage);
        console.error("Failed to fetch pending documents:", error);
      } finally {
        setPendingLoading(false);
      }
    },
    [itemsPerPage, pendingFilters]
  );

  /**
   * Fetch completed documents
   */
  const fetchCompleted = useCallback(
    async (page: number): Promise<void> => {
      setCompletedLoading(true);
      setError(null);

      try {
        const result = await getCompletedDocumentsUseCase.execute(
          page,
          itemsPerPage,
          completedFilters
        );

        setCompletedDocuments(result.documents);
        setCompletedCurrentPage(result.pagination.currentPage);
        setCompletedTotalPages(result.pagination.totalPages);
        setCompletedTotalItems(result.pagination.total);
        setCompletedHasMore(result.pagination.hasMore);
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to load completed documents";
        setError(errorMessage);
        console.error("Failed to fetch completed documents:", error);
      } finally {
        setCompletedLoading(false);
      }
    },
    [itemsPerPage, completedFilters]
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Fetch both pending and completed documents in parallel
   */
  const fetchAllDocuments = useCallback(
    async (
      pendingPage: number = 1,
      completedPage: number = 1
    ): Promise<void> => {
      setPendingLoading(true);
      setCompletedLoading(true);
      setError(null);

      try {
        await Promise.all([
          getPendingDocumentsUseCase
            .execute(pendingPage, itemsPerPage, pendingFilters)
            .then((result) => {
              setPendingDocuments(result.documents);
              setPendingCurrentPage(result.pagination.currentPage);
              setPendingTotalPages(result.pagination.totalPages);
              setPendingTotalItems(result.pagination.total);
              setPendingHasMore(result.pagination.hasMore);
            }),
          getCompletedDocumentsUseCase
            .execute(completedPage, itemsPerPage, completedFilters)
            .then((result) => {
              setCompletedDocuments(result.documents);
              setCompletedCurrentPage(result.pagination.currentPage);
              setCompletedTotalPages(result.pagination.totalPages);
              setCompletedTotalItems(result.pagination.total);
              setCompletedHasMore(result.pagination.hasMore);
            }),
        ]);
      } catch (error: any) {
        const errorMessage = error.message || "Failed to load documents";
        setError(errorMessage);
        console.error("Failed to fetch documents:", error);
      } finally {
        setPendingLoading(false);
        setCompletedLoading(false);
      }
    },
    [itemsPerPage, pendingFilters, completedFilters]
  );

  /**
   * Fetch both pending and completed documents silently (without loading states)
   * Used for auto-refresh to avoid showing loaders
   */
  const fetchAllDocumentsSilent = useCallback(
    async (
      pendingPage: number = 1,
      completedPage: number = 1
    ): Promise<void> => {
      // Don't set loading states or error for silent refresh
      try {
        await Promise.all([
          getPendingDocumentsUseCase
            .execute(pendingPage, itemsPerPage, pendingFilters)
            .then((result) => {
              setPendingDocuments(result.documents);
              setPendingCurrentPage(result.pagination.currentPage);
              setPendingTotalPages(result.pagination.totalPages);
              setPendingTotalItems(result.pagination.total);
              setPendingHasMore(result.pagination.hasMore);
            }),
          getCompletedDocumentsUseCase
            .execute(completedPage, itemsPerPage, completedFilters)
            .then((result) => {
              setCompletedDocuments(result.documents);
              setCompletedCurrentPage(result.pagination.currentPage);
              setCompletedTotalPages(result.pagination.totalPages);
              setCompletedTotalItems(result.pagination.total);
              setCompletedHasMore(result.pagination.hasMore);
            }),
        ]);
      } catch (error: any) {
        // Silently handle errors - don't update error state or show to user
        console.error("Silent refresh failed:", error);
      }
    },
    [itemsPerPage, pendingFilters, completedFilters]
  );

  // Load and refresh each list independently when its own filters change.
  useEffect(() => {
    fetchPending(1);
  }, [fetchPending]);

  useEffect(() => {
    fetchCompleted(1);
  }, [fetchCompleted]);

  return {
    // Pending
    pendingDocuments,
    pendingPagination: {
      currentPage: pendingCurrentPage,
      totalPages: pendingTotalPages,
      totalItems: pendingTotalItems,
      hasMore: pendingHasMore,
    },
    pendingLoading,
    fetchPending,

    // Completed
    completedDocuments,
    completedPagination: {
      currentPage: completedCurrentPage,
      totalPages: completedTotalPages,
      totalItems: completedTotalItems,
      hasMore: completedHasMore,
    },
    completedLoading,
    fetchCompleted,

    // Fetch both
    fetchAllDocuments,
    fetchAllDocumentsSilent,

    // Common
    error,
    clearError,
  };
};
